/**
 * Описывает структуру поля формы.
 */
interface Field {
    /**
     * Валидирует значение поля.
     * @param {boolean | undefined} force - Признак принудительной валидации (например, при сабмите)
     * @returns {string[]} Массив сообщений об ошибках (пустой, если всё ок)
     */
    validate: (force: boolean | undefined) => string[];

    /**
     * Сбрасывает состояние поля (например, ошибки).
     */
    reset: () => void;

    /**
     * Заголовок (название) поля.
     */
    title: string;
}

/**
 * Описывает структуру объекта ошибок:
 * ключ (имя поля) -> { value: string[]; title: string }
 */
interface Errors {
    [key: string]: { value: string[]; title: string };
}

/**
 * Композиционная функция для управления состоянием формы:
 * - хранит зарегистрированные поля
 * - хранит и обновляет ошибки
 * - умеет вызывать валидацию и сбрасывать её
 *
 * @returns Набор методов и реактивных свойств:
 *  - registerField: зарегистрировать поле
 *  - unregisterField: удалить поле
 *  - validate: валидировать все поля
 *  - resetValidation: сбросить ошибки везде
 *  - errors: объект текущих ошибок (reactive)
 *  - isValid: вычисляемое, true если нет ошибок ни в одном поле
 *  - updateFormValidation: вручную изменить ошибки поля
 */
export function useForm() {
    /**
     * Реактивный объект со всеми полями (key -> Field).
     */
    const fields = reactive<Record<string, Field>>({})

    /**
     * Реактивный объект с ошибками (key -> { title, value[] }).
     */
    const errors = reactive<Errors>({})

    /**
     * Вычисляемое, показывает, нет ли ошибок в форме (errors.value во всех полях пуст).
     */
    const isValid = computed(() => {
        return Object.values(errors).every(fieldError => fieldError.value.length === 0)
    })

    /**
     * Регистрирует новое поле в форме, сбрасывает его ошибки и сразу запускает валидацию (без force).
     * @param {string} key - ключ (идентификатор) поля
     * @param {string} title - заголовок поля
     * @param {Field} field - объект поля, содержащий validate/reset/title
     */
    const registerField = (key: string, title: string, field: Field) => {
        fields[key] = { ...field, title }
        errors[key] = { title, value: [] }
        validate(false)
    }

    /**
     * Удаляет поле из формы и соответствующие ему ошибки.
     * @param {string} key - ключ поля
     */
    const unregisterField = (key: string) => {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete fields[key]
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete errors[key]
    }

    /**
     * Валидирует все зарегистрированные поля.
     * Для каждого поля вызывает field.validate(force) и записывает результат в errors.
     *
     * @param {boolean} force - если true, принудительная валидация
     * @returns {boolean} true, если последнее проверенное поле не имеет ошибок, иначе false
     */
    const validate = (force: boolean): boolean => {
        let fieldErrors: string[] = []
        for (const key in fields) {
            fieldErrors = fields[key].validate(force)
            errors[key] = { title: fields[key].title, value: fieldErrors }
        }

        return !fieldErrors?.length
    }

    /**
     * Сбрасывает ошибки у всех полей (очищает массив errors).
     */
    const resetValidation = () => {
        for (const key in fields) {
            errors[key] = { title: fields[key].title, value: [] }
        }
    }

    /**
     * Обновляет ошибки конкретного поля.
     * @param {string} key - ключ поля
     * @param {string} title - заголовок поля
     * @param {string[]} errorsIn - массив новых ошибок
     */
    const updateFormValidation = (key: string, title: string, errorsIn: string[]) => {
        try{
            errors[key] = { title: fields[key].title, value: errorsIn }
        } catch(error) {
            console.error('error when registering field', title, key, 'is', error)
        }
    }

    return {
        registerField,
        unregisterField,
        validate,
        resetValidation,
        errors,
        isValid,
        updateFormValidation
    }
}
